<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex justify-between items-start mb-8">
      <div>
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">CRA Readiness</h1>
        <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">EU Cyber Resilience Act compliance status</p>
      </div>
      <button @click="fetchReadiness" :disabled="loading" class="btn-primary text-sm">
        {{ loading ? 'Checking...' : 'Refresh' }}
      </button>
    </div>

    <div v-if="loading && !data" class="flex items-center justify-center py-16">
      <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 dark:border-gray-700 border-t-blue-500"></div>
    </div>

    <template v-else-if="data">
      <!-- Score Overview -->
      <div class="card p-6 mb-6">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div class="flex items-center gap-4">
            <div class="relative w-20 h-20">
              <svg class="w-20 h-20 -rotate-90" viewBox="0 0 36 36">
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" stroke="currentColor" stroke-width="3" class="text-gray-200 dark:text-gray-700" />
                <path d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" :stroke="scoreColor" stroke-width="3" :stroke-dasharray="`${scorePercent}, 100`" stroke-linecap="round" />
              </svg>
              <span class="absolute inset-0 flex items-center justify-center text-lg font-bold" :class="scoreTextClass">
                {{ data.score }}/{{ data.max_score }}
              </span>
            </div>
            <div>
              <h2 class="text-xl font-semibold text-gray-900 dark:text-white">{{ scoreLabel }}</h2>
              <p class="text-sm text-gray-500 dark:text-gray-400">{{ passCount }} of {{ data.max_score }} checks passing</p>
            </div>
          </div>
          <div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
            <p class="text-xs font-medium text-amber-800 dark:text-amber-300 uppercase tracking-wide">Next Deadline</p>
            <p class="text-sm font-semibold text-amber-900 dark:text-amber-200 mt-0.5">{{ data.next_deadline }}</p>
          </div>
        </div>
      </div>

      <!-- Status Counts -->
      <div class="grid grid-cols-3 gap-4 mb-6">
        <div class="card p-4 text-center">
          <p class="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{{ passCount }}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Passing</p>
        </div>
        <div class="card p-4 text-center">
          <p class="text-2xl font-bold text-amber-600 dark:text-amber-400">{{ warnCount }}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Warnings</p>
        </div>
        <div class="card p-4 text-center">
          <p class="text-2xl font-bold text-red-600 dark:text-red-400">{{ failCount }}</p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Failing</p>
        </div>
      </div>

      <!-- Checks by Category -->
      <div v-for="cat in categories" :key="cat" class="mb-6">
        <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">{{ cat }}</h3>
        <div class="space-y-2">
          <div v-for="check in checksByCategory(cat)" :key="check.id"
            class="card px-4 py-3 flex items-start gap-3">
            <div class="flex-shrink-0 mt-0.5">
              <span v-if="check.status === 'pass'" class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                <svg class="w-3 h-3 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
              </span>
              <span v-else-if="check.status === 'warn'" class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/30">
                <svg class="w-3 h-3 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M12 9v2m0 4h.01"/></svg>
              </span>
              <span v-else class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/30">
                <svg class="w-3 h-3 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"/></svg>
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-gray-900 dark:text-white">{{ check.title }}</p>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{{ check.description }}</p>
            </div>
            <div class="flex items-center gap-3 flex-shrink-0">
              <span v-if="check.detail" class="text-xs text-gray-400 dark:text-gray-500 hidden sm:inline">{{ check.detail }}</span>
              <router-link
                v-if="check.status !== 'pass' && actionFor(check.id)"
                :to="actionFor(check.id).route"
                class="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors whitespace-nowrap"
                :class="check.status === 'fail'
                  ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/40'
                  : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40'"
              >
                {{ actionFor(check.id).label }}
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/></svg>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="text-center py-16 text-gray-500 dark:text-gray-400">
      Failed to load CRA readiness data.
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import axios from 'axios'

const getAPIURL = () => {
  const envUrl = process.env.VUE_APP_API_URL || 'http://127.0.0.1:10000'
  const cleanUrl = envUrl.replace(/\/$/, '')
  return cleanUrl.endsWith('/api/v1') ? cleanUrl : `${cleanUrl}/api/v1`
}

export default {
  name: 'CRADashboardView',
  setup() {
    const store = useStore()
    const route = useRoute()
    const data = ref(null)
    const loading = ref(false)

    const tenantId = computed(() => route.params.tenantId || store.getters.currentTenantId)

    const checkActions = computed(() => {
      const t = tenantId.value
      if (!t) return {}
      return {
        products_defined:    { route: `/tenants/${t}/products`,            label: 'Add Product' },
        sbom_coverage:       { route: `/tenants/${t}/products`,            label: 'Upload SBOMs' },
        vuln_disclosure:     { route: `/tenants/${t}/vulnerabilities`,     label: 'Manage Vulns' },
        security_contact:    { route: `/tenants/${t}/products`,            label: 'Edit Products' },
        vdp:                 { route: `/tenants/${t}/products`,            label: 'Set VDP URL' },
        support_lifecycle:   { route: `/tenants/${t}/products`,            label: 'Set End-of-Support' },
        cra_compliance:      { route: `/tenants/${t}/compliances`,         label: 'Add Compliance' },
        security_practices:  { route: `/tenants/${t}/security-categories`, label: 'Configure' },
        public_docs:         { route: `/tenants/${t}/documents`,           label: 'Manage Docs' },
        csaf_vex:            { route: `/tenants/${t}/products`,            label: 'Add Products & Vulns' },
        incident_tracking:   { route: `/tenants/${t}/incidents`,           label: 'Manage Incidents' },
        enisa_deadlines:     { route: `/tenants/${t}/enisa-reporting`,     label: 'ENISA Reports' },
        reporting_lifecycle: { route: `/tenants/${t}/enisa-reporting`,     label: 'ENISA Reports' },
      }
    })

    const actionFor = (checkId) => checkActions.value[checkId] || null

    const fetchReadiness = async () => {
      if (!tenantId.value) return
      loading.value = true
      try {
        const res = await axios.get(`${getAPIURL()}/admin/tenants/${tenantId.value}/cra-readiness`, {
          headers: { Authorization: `Bearer ${store.state.token}` }
        })
        data.value = res.data
      } catch (e) {
        console.error('Failed to fetch CRA readiness:', e)
      } finally {
        loading.value = false
      }
    }

    const scorePercent = computed(() => data.value ? (data.value.score / data.value.max_score) * 100 : 0)
    const scoreColor = computed(() => {
      const p = scorePercent.value
      if (p >= 80) return '#10b981'
      if (p >= 50) return '#f59e0b'
      return '#ef4444'
    })
    const scoreTextClass = computed(() => {
      const p = scorePercent.value
      if (p >= 80) return 'text-emerald-600 dark:text-emerald-400'
      if (p >= 50) return 'text-amber-600 dark:text-amber-400'
      return 'text-red-600 dark:text-red-400'
    })
    const scoreLabel = computed(() => {
      const p = scorePercent.value
      if (p >= 80) return 'Good Standing'
      if (p >= 50) return 'Needs Attention'
      return 'Not Ready'
    })

    const passCount = computed(() => data.value?.checks.filter(c => c.status === 'pass').length || 0)
    const warnCount = computed(() => data.value?.checks.filter(c => c.status === 'warn').length || 0)
    const failCount = computed(() => data.value?.checks.filter(c => c.status === 'fail').length || 0)

    const categories = computed(() => {
      if (!data.value) return []
      const order = ['Product Security', 'SBOM', 'Vulnerability Management', 'Machine-Readable', 'Compliance', 'Documentation', 'Incident Response']
      const found = [...new Set(data.value.checks.map(c => c.category))]
      return order.filter(o => found.includes(o))
    })

    const checksByCategory = (cat) => data.value?.checks.filter(c => c.category === cat) || []

    onMounted(fetchReadiness)

    return { data, loading, fetchReadiness, scorePercent, scoreColor, scoreTextClass, scoreLabel, passCount, warnCount, failCount, categories, checksByCategory, actionFor }
  }
}
</script>
